/**
 * Reusable password validation rules for Ant Design forms
 
 *These rules match the backend validation in:
 * - app/models/concerns/password_validatable.rb
 * - config/initializers/devise.rb
 */

export const basePasswordRules = [
  {
    required: true,
    message: 'Please enter a new password',
  },
];

export const passwordRules = [
  {
    min: 12,
    message: 'Must be a minimum of 12 characters',
  },
  {
    max: 64,
    message: 'Must be a maximum of 64 characters',
  },
  {
    pattern: /(?=.*[a-z])/,
    message: 'Must contain at least one lowercase letter (a-z)',
  },
  {
    pattern: /(?=.*[A-Z])/,
    message: 'Must contain at least one uppercase letter (A-Z)',
  },
  {
    pattern: /(?=.*\d)/,
    message: 'Must contain at least one digit (0-9)',
  },
  {
    pattern: /(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/,
    message: 'Must contain at least one special character (e.g., @#$%^&*()_)',
  },
];

export const getPasswordRules = () => {
  return [...basePasswordRules, ...passwordRules];
};

/**
 * Get password confirmation validation rules
 * - Only shows error when passwords don't match (no required validation)
 *
 * @param {Function} getFieldValue
 * @param {Object} options
 * @param {string} options.validateTrigger
 * @param {string} options.passwordFieldName
 * @returns {Array}
 */

export const getPasswordConfirmationRules = (
  getFieldValue,
  options = { validateTrigger: 'change', passwordFieldName: 'password' },
) => {
  const passwordFieldName = options.passwordFieldName || 'password';
  const validateTrigger = options.validateTrigger || 'change';

  return [
    {
      validator: (_rule, value, callback) => {
        try {
          if (!value || value === '') {
            callback();
            return;
          }

          let passwordValue;
          try {
            passwordValue = getFieldValue(passwordFieldName);
          } catch (error) {
            console.error('Error getting password field value:', error);
            callback();
            return;
          }
          // This prevents showing errors when user is still filling out the form
          if (passwordValue === undefined || passwordValue === null || passwordValue === '') {
            callback();
            return;
          }

          // Only show error if they don't match
          if (value !== passwordValue) {
            callback('Password must match');
          } else {
            callback();
          }
        } catch (error) {
          console.error('Password confirmation validation error:', error);
          // On unexpected error, don't block form submission
          // The form will validate on submit anyway
          callback();
        }
      },
      validateTrigger,
    },
  ];
};

/**
 * onChange handler for the password field that re-validates the confirmation field
 *
 * @param {Object} form
 * @param {string} confirmationFieldName
 * @param {string} passwordFieldName
 * @returns {Function}
 */

export const createPasswordOnChangeHandler = (
  form,
  confirmationFieldName = 'password_confirmation',
  passwordFieldName = 'password',
) => {
  return (e) => {
    const newPasswordValue = e.target.value;

    form.setFieldsValue({ [passwordFieldName]: newPasswordValue });

    form.validateFields([confirmationFieldName], { force: true }, (errors) => {
      if (!errors || !errors[confirmationFieldName]) {
        const confirmationValue = form.getFieldValue(confirmationFieldName);
        form.setFields({
          [confirmationFieldName]: {
            value: confirmationValue,
            errors: undefined,
          },
        });
      }
    });
  };
};
